import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, GripVertical, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useLanguage } from "@/contexts/LanguageContext";

type FieldType = "text" | "email" | "phone" | "number" | "textarea" | "select" | "checkbox";

interface CheckoutField {
  id: string; label: string; type: FieldType; required: boolean; placeholder: string; options: string; sort_order: number;
}

const CheckoutSettings = () => {
  const { profile } = useAuth();
  const { t } = useLanguage();
  const [fields, setFields] = useState<CheckoutField[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const orgId = profile?.organisation_id;

  const fieldTypes: { value: FieldType; label: string }[] = [
    { value: "text", label: t("checkout.type_text") },
    { value: "email", label: t("checkout.type_email") },
    { value: "phone", label: t("checkout.type_phone") },
    { value: "number", label: t("checkout.type_number") },
    { value: "textarea", label: t("checkout.type_textarea") },
    { value: "select", label: t("checkout.type_select") },
    { value: "checkbox", label: t("checkout.type_checkbox") },
  ];

  useEffect(() => {
    if (!orgId) return;
    const fetchFields = async () => {
      setLoading(true);
      const { data, error } = await supabase.from("checkout_fields").select("id, label, type, required, placeholder, options, sort_order").eq("organisation_id", orgId).order("sort_order", { ascending: true });
      if (error) { toast.error(t("checkout.load_failed")); }
      else { setFields((data || []).map((f: any) => ({ id: f.id, label: f.label, type: f.type as FieldType, required: f.required, placeholder: f.placeholder || "", options: f.options || "", sort_order: f.sort_order }))); }
      setLoading(false);
    };
    fetchFields();
  }, [orgId]);

  const addField = () => { setFields([...fields, { id: crypto.randomUUID(), label: "", type: "text", required: false, placeholder: "", options: "", sort_order: fields.length }]); };
  const updateField = (id: string, updates: Partial<CheckoutField>) => { setFields(fields.map((f) => (f.id === id ? { ...f, ...updates } : f))); };
  const removeField = (id: string) => { setFields(fields.filter((f) => f.id !== id)); };

  const handleSave = async () => {
    if (!orgId) return;
    if (fields.some((f) => !f.label.trim())) { toast.error(t("checkout.label_required")); return; }
    setSaving(true);
    try {
      await supabase.from("checkout_fields").delete().eq("organisation_id", orgId);
      if (fields.length > 0) {
        const rows = fields.map((f, idx) => ({ id: f.id, organisation_id: orgId, label: f.label, type: f.type, required: f.required, placeholder: f.placeholder, options: f.options, sort_order: idx }));
        const { error } = await supabase.from("checkout_fields").insert(rows);
        if (error) throw error;
      }
      toast.success(t("checkout.saved"));
    } catch (e: any) { toast.error(e.message || "Unknown error"); }
    finally { setSaving(false); }
  };

  if (loading) { return (<div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>); }

  return (
    <div>
      <h1 className="text-xl font-bold text-foreground mb-1">{t("checkout.title")}</h1>
      <p className="text-sm text-muted-foreground mb-6">{t("checkout.desc")}</p>

      <section className="rounded-xl border border-border bg-card p-6 shadow-card mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-foreground">{t("checkout.fields")}</h2>
          <Button variant="outline" size="sm" onClick={addField}><Plus className="h-4 w-4 mr-1" />{t("checkout.add_field")}</Button>
        </div>

        {fields.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border py-8 text-center">
            <p className="text-sm text-muted-foreground">{t("checkout.no_fields")}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {fields.map((field, idx) => (
              <div key={field.id} className="rounded-xl border border-border p-4 bg-muted/30">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <GripVertical className="h-4 w-4 text-muted-foreground/50" />
                    <span className="text-xs font-medium text-muted-foreground">{t("checkout.field_num")} {idx + 1}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-2">
                      <Switch checked={field.required} onCheckedChange={(v) => updateField(field.id, { required: v })} />
                      <span className="text-xs text-muted-foreground">{t("checkout.required")}</span>
                    </div>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => removeField(field.id)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
                <div className="grid gap-3 md:grid-cols-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">{t("checkout.label")}</Label>
                    <Input value={field.label} onChange={(e) => updateField(field.id, { label: e.target.value })} placeholder={t("checkout.label_placeholder")} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">{t("checkout.type")}</Label>
                    <Select value={field.type} onValueChange={(v: FieldType) => updateField(field.id, { type: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{fieldTypes.map((ft) => (<SelectItem key={ft.value} value={ft.value}>{ft.label}</SelectItem>))}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">{t("checkout.placeholder")}</Label>
                    <Input value={field.placeholder} onChange={(e) => updateField(field.id, { placeholder: e.target.value })} placeholder={t("checkout.placeholder_text")} />
                  </div>
                </div>
                {field.type === "select" && (
                  <div className="mt-3 space-y-1.5">
                    <Label className="text-xs">{t("checkout.dropdown_options")}</Label>
                    <Input value={field.options} onChange={(e) => updateField(field.id, { options: e.target.value })} placeholder={t("checkout.dropdown_placeholder")} />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      <div className="flex items-center gap-3">
        <Button onClick={handleSave} disabled={saving}>
          {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          {t("checkout.save")}
        </Button>
      </div>
    </div>
  );
};

export default CheckoutSettings;
